import React, {
    type Dispatch,
    type MouseEvent,
    type SetStateAction,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import loadable from '@loadable/component';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { type CheckoutCategorySubtype, ClickType } from '@jsmdg/tracking';
import {
    Breakpoint,
    Button,
    ButtonColor,
    ButtonSize,
    ButtonVariant,
    Dropdown,
    ExclamationTriangleIcon,
    useBreakpoint,
} from '@jsmdg/yoshi';
import { type SelectOption } from '@jsmdg/yoshi/dist/types/selectOption';
import { ProductPromotionType } from '../../../shared/enums';
import { hasDiscountCodeCampaignBadge } from '../../../shared/hasDiscountCodeCampaignBadge';
import {
    getBookableLocationsCount,
    isBox,
    isExperience,
    isValueVoucherWithPriceRange,
} from '../../../shared/product-safeguards';
import { type ValueVoucherWithPriceRange } from '../../../shared/types';
import { isAddedToCartDirectly, isDeliveryFormatGroup } from '../../helper/deliveryFormat';
import { formatPrice } from '../../helper/formatPrice';
import { type UseBuyboxReturn } from '../../hooks/useBuybox';
import { usePayPalExpressError } from '../../hooks/usePayPalExpressError';
import { usePrice } from '../../hooks/usePrice';
import { useProduct } from '../../hooks/useProduct';
import { SellOption } from '../../types/enums/sellOption';
import { type ProductDetailContext } from '../../types/types';
import { ExperienceGuaranteeItem } from '../Buybox/ExperienceGuaranteeItem';
import { ClubDiscountBadge } from '../ClubDiscountBadge';
import { DeliveryFormatQuantity } from '../DeliveryFormatQuantity';
import { DiscountCodeCampaignBadge } from '../DiscountCodeCampaignBadge';
import { FriendsDiscountBadge } from '../FriendsDiscountBadge';
import { PaybackPoints } from '../PaybackPoints';
import { PayPalBuyboxButton } from '../PayPalBuyboxButton';
import { Price } from '../Price';
import { StrikePriceInfo } from '../Price/StrikePriceInfo';
import { ProductGroup } from '../ProductGroup';
import { trackDropDownOpened } from '../ProductGroup/productGroup.tracking';
import { RedeemInfo } from '../RedeemInfo';
import { VoucherAndTicketSelector } from '../VoucherAndTicketSelector';
import styles from '../Buybox/Buybox.module.scss';

const LoadableShareContainer = loadable(async () => import('../ShareContainer/ShareContainer'));

const messages = defineMessages({
    friendsDiscountSelectHeader: {
        defaultMessage: 'Gutschein kaufen',
    },
    voucherAmount: {
        defaultMessage: 'Gutscheinbetrag',
    },
});
const DEFAULT_PRICE_VALUE_VOUCHER_RANGE = [
    20, 40, 50, 60, 80, 100, 120, 140, 150, 160, 180, 200, 250, 300, 350, 400, 500, 600,
];

// eslint-disable-next-line complexity
const BuyboxBody = ({
    buyboxProps,
    callToActionText,
    handleBuyboxButtonClick,
    isCheckoutDisabled,
    setTargetElement,
    showPayPalButton = false,
}: {
    readonly setTargetElement: Dispatch<SetStateAction<HTMLElement | null>>;
    readonly isCheckoutDisabled: boolean;
    readonly callToActionText: string;
    readonly showPayPalButton?: boolean;
    readonly buyboxProps: UseBuyboxReturn;
    readonly handleBuyboxButtonClick: (
        event?: MouseEvent<HTMLButtonElement>,
        clickType?: ClickType,
        categorySubtype?: CheckoutCategorySubtype,
        addVoucherToCart?: boolean,
    ) => void;
}) => {
    const { handlePayPalAddToCartError, purchaseType } = buyboxProps;
    const { price, setAmount } = usePrice();

    const intl = useIntl();
    const product = useProduct();
    const bookableLocationsCount = getBookableLocationsCount(product);
    const upToSMBreakpoint = useBreakpoint(Breakpoint.SM);

    const { isBoxProductWithQrCodeLink } = useFragmentContext<ProductDetailContext>();

    const handlePayPalError = usePayPalExpressError();

    const checkoutButtonRef = useRef<HTMLButtonElement>(null);
    const [checkoutButtonHeight, setCheckoutButtonHeight] = useState(0);

    useLayoutEffect(() => {
        if (!showPayPalButton) {
            setCheckoutButtonHeight(0);
            return;
        }

        if (checkoutButtonRef.current) {
            setCheckoutButtonHeight(checkoutButtonRef.current.offsetHeight);
        }
    }, [showPayPalButton, callToActionText, isCheckoutDisabled]);

    const { eligibleForPayback, hasFriendsDiscount, productGroup, productId, type, url } = product;

    const isDesktop = useBreakpoint(Breakpoint.XS);
    const hasCampaignBadge = hasDiscountCodeCampaignBadge(
        product.discountBadge,
        Boolean(product.originalPrice),
    );
    const isValueVoucherFlexible = isValueVoucherWithPriceRange(product);
    const isClubDeal = product.promotionType === ProductPromotionType.CustomerClub;
    const hasDeliveryFormats = isDeliveryFormatGroup(productGroup);
    const addsToCartDirectly = isAddedToCartDirectly(product);

    const showCampaignBadge = !isBoxProductWithQrCodeLink && hasCampaignBadge;
    const showFriendsDiscountBadge =
        !showCampaignBadge && !isBoxProductWithQrCodeLink && !isBox(product) && hasFriendsDiscount;
    const showClubDealBadge = !showCampaignBadge && !showFriendsDiscountBadge && isClubDeal;

    const createValueVoucherFlexibleItems = (
        valueVoucherWithPriceRange: ValueVoucherWithPriceRange,
    ): SelectOption[] => {
        const min = valueVoucherWithPriceRange.priceRange.min.amount;
        const max = valueVoucherWithPriceRange.priceRange.max.amount;
        const currencyCode = valueVoucherWithPriceRange.priceRange.min.currencyCode;

        return DEFAULT_PRICE_VALUE_VOUCHER_RANGE.filter(value => value >= min && value <= max).map(
            valueNumber => ({
                label: formatPrice(intl.locale, valueNumber, currencyCode),
                value: `${valueNumber}`,
            }),
        );
    };

    const showNoAvailableAppointmentExclamation =
        isExperience(product) &&
        !bookableLocationsCount &&
        product.sellOption === SellOption.TicketOnly;

    const hideCallToAction =
        !hasDeliveryFormats &&
        ((!bookableLocationsCount && isExperience(product)) ||
            (isExperience(product) && product.sellOption !== SellOption.TicketOnly));
    return (
        <div
            data-testid="stickyVoucherAndTicketSelectionWrapper"
            className={classNames(styles.voucherAndTicketSelectionWrapper, {
                'sticky-sm-top': isExperience(product),
            })}
        >
            <div className="pb-3x">
                <Price purchaseType={purchaseType} />
            </div>

            {hasDeliveryFormats && (
                <>
                    {showCampaignBadge && <DiscountCodeCampaignBadge className="pb-1x" />}
                    {showFriendsDiscountBadge && <FriendsDiscountBadge className="pb-1x" isSolid />}
                    {showClubDealBadge && <ClubDiscountBadge className="mb-1x" />}
                    <ProductGroup
                        productType={type}
                        selectedProductId={productId}
                        items={productGroup}
                    />
                    {!isBox(product) && <DeliveryFormatQuantity />}
                </>
            )}

            {isExperience(product) && !hasDeliveryFormats && (
                <VoucherAndTicketSelector
                    buyboxProps={buyboxProps}
                    minParticipants={product.minParticipants}
                    maxParticipants={product.maxParticipants}
                    isCheckoutDisabled={
                        isCheckoutDisabled || product.sellOption === SellOption.TicketOnly
                    }
                    showPayPalButton={showPayPalButton}
                    setTargetElement={setTargetElement}
                    isClubDeal={isClubDeal}
                />
            )}
            {showNoAvailableAppointmentExclamation ? (
                <div className="px-3x pt-3x px-xs-6x">
                    <strong className="d-flex">
                        <ExclamationTriangleIcon
                            key="exclamationTriangleTop"
                            className={styles.exclamationMark}
                        />
                        <FormattedMessage defaultMessage="Derzeit sind keine Termine verfügbar." />
                    </strong>
                </div>
            ) : null}
            {!isBoxProductWithQrCodeLink && (
                <>
                    {isBox(product) && !hasDeliveryFormats && (
                        <div className="pt-3x">
                            {hasCampaignBadge && <DiscountCodeCampaignBadge className="pb-0-5x" />}
                            <RedeemInfo />
                        </div>
                    )}
                    {hasDeliveryFormats && (
                        <div className="py-3x">
                            <RedeemInfo />
                        </div>
                    )}
                    {isValueVoucherFlexible && (
                        <div className="pb-3x">
                            <Dropdown
                                label={intl.formatMessage(messages.voucherAmount)}
                                ariaLabel={intl.formatMessage(messages.voucherAmount)}
                                options={createValueVoucherFlexibleItems(product)}
                                value={`${price.gross.amount}`}
                                dataTestId="select-valuevoucher-flexible"
                                className="d-block"
                                overlayClasses={styles.box}
                                onClick={() => {
                                    trackDropDownOpened();
                                }}
                                onChange={value => {
                                    setAmount(Number(value));
                                }}
                            />
                        </div>
                    )}
                </>
            )}
            {!isExperience(product) && (
                <div
                    ref={setTargetElement as (el: HTMLElement | null) => void}
                    className={styles.sentinel}
                />
            )}

            <div
                className="d-flex flex-wrap justify-content-space-between align-items-center pb-3x position-relative"
                data-testid="price-and-add-to-cart-button-wrapper"
            >
                <div
                    className={classNames({
                        [styles.checkoutButtonsRow]: showPayPalButton,
                        'w-100': !showPayPalButton,
                        'd-none': hideCallToAction,
                        'mt-2x px-2x': !addsToCartDirectly,
                    })}
                >
                    <Button
                        variant={
                            addsToCartDirectly ? ButtonVariant.Contained : ButtonVariant.Outlined
                        }
                        ref={checkoutButtonRef}
                        data-testid="checkout-button"
                        onClick={(event: MouseEvent<HTMLButtonElement>) =>
                            handleBuyboxButtonClick(event, undefined, undefined, addsToCartDirectly)
                        }
                        disabled={isCheckoutDisabled}
                        className={classNames('text-nowrap', {
                            'w-100': !showPayPalButton,
                        })}
                        color={ButtonColor.Brand}
                        a11yText={callToActionText}
                        size={isDesktop ? ButtonSize.Large : ButtonSize.Small}
                    >
                        {callToActionText}
                    </Button>

                    {showPayPalButton && addsToCartDirectly && (
                        <PayPalBuyboxButton
                            buttonHeight={checkoutButtonHeight}
                            clickType={ClickType.DefaultUpper}
                            productId={product.productId}
                            quantity={price.multiplier}
                            disableButton={isCheckoutDisabled}
                            onAddToCartError={handlePayPalAddToCartError}
                            handleError={handlePayPalError}
                        />
                    )}
                </div>
                <div className="d-sm-none w-100" data-testid="share-container">
                    <LoadableShareContainer productUrl={url} />
                </div>
                {!isExperience(product) && !isBox(product) && <ExperienceGuaranteeItem />}
                {eligibleForPayback && (
                    <PaybackPoints
                        price={price.gross.amount}
                        selectedSeatCount={price.multiplier}
                    />
                )}
            </div>
            {upToSMBreakpoint && !isClubDeal && <StrikePriceInfo numTickets={price.multiplier} />}
        </div>
    );
};

export { BuyboxBody };
